(() => {
class Fuel07Carousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) {
      if (this.track) this.resizeObserver?.observe(this.track);
      this.updateNavigation();
      return;
    }

    this.initialized = true;
    this.track = this.querySelector('[data-fuel-07-track]');
    this.previousButton = this.querySelector('[data-fuel-07-previous]');
    this.nextButton = this.querySelector('[data-fuel-07-next]');
    this.status = this.querySelector('[data-fuel-07-status]');
    this.navigation = this.querySelector('.fuel-07-carousel__navigation');

    if (!this.track || !this.previousButton || !this.nextButton) return;

    this.previousButton.addEventListener('click', () => this.scroll(-1));
    this.nextButton.addEventListener('click', () => this.scroll(1));
    this.track.addEventListener('scroll', () => window.requestAnimationFrame(() => this.updateNavigation()));
    this.resizeObserver = new ResizeObserver(() => this.updateNavigation());
    this.resizeObserver.observe(this.track);
    this.updateNavigation();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  usesPositionNavigation() {
    return document.documentElement.matches(
      '.ab-fuel-07-b, .ab-fuel-07-c, .ab-fuel-08-b, .ab-fuel-08-c, .shopify-design-mode',
    );
  }

  getScrollPositions() {
    const items = Array.from(this.track.querySelectorAll('.fuel-07-carousel__item'));
    const firstItem = items[0];
    if (!firstItem) return [0];

    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const firstOffset = firstItem.offsetLeft;
    const positions = [];

    for (const item of items) {
      const position = Math.min(maxScroll, Math.max(0, item.offsetLeft - firstOffset));
      const previousPosition = positions[positions.length - 1];

      if (previousPosition === undefined || Math.abs(position - previousPosition) > 1) {
        positions.push(position);
      }
    }

    return positions.length > 0 ? positions : [0];
  }

  scroll(direction) {
    const firstItem = this.track.querySelector('.fuel-07-carousel__item');
    if (!firstItem) return;

    if (this.usesPositionNavigation()) {
      const positions = this.getScrollPositions();
      const currentPosition = this.track.scrollLeft;
      const targetPosition =
        direction > 0
          ? (positions.find((position) => position > currentPosition + 1) ?? positions[positions.length - 1])
          : (positions.slice().reverse().find((position) => position < currentPosition - 1) ?? positions[0]);

      this.track.scrollTo({ left: targetPosition, behavior: 'smooth' });
      return;
    }

    const gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({ left: direction * (firstItem.offsetWidth + gap), behavior: 'smooth' });
  }

  updateNavigation() {
    if (!this.track || !this.previousButton || !this.nextButton) return;

    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const hasOverflow = maxScroll > 1;
    const firstItem = this.track.querySelector('.fuel-07-carousel__item');
    const atEnd = this.track.scrollLeft >= maxScroll - 1;
    const usePositionNavigation = this.usesPositionNavigation();
    let current;
    let total;

    if (usePositionNavigation) {
      const positions = this.getScrollPositions();
      let currentIndex = 0;

      for (let index = 1; index < positions.length; index += 1) {
        if (Math.abs(positions[index] - this.track.scrollLeft) < Math.abs(positions[currentIndex] - this.track.scrollLeft)) {
          currentIndex = index;
        }
      }

      total = positions.length;
      current = atEnd ? total : currentIndex + 1;
    } else {
      const gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      const itemStep = (firstItem?.offsetWidth ?? 1) + gap;

      total = this.track.children.length;
      current = atEnd ? total : Math.min(total, Math.round(this.track.scrollLeft / itemStep) + 1);
    }

    this.previousButton.disabled = this.track.scrollLeft <= 1;
    this.nextButton.disabled = atEnd;
    this.navigation?.toggleAttribute('hidden', !hasOverflow);

    if (this.status) {
      this.status.textContent = `${current} / ${total}`;
    }
  }
}

class Fuel07Tabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.tabs = Array.from(this.querySelectorAll('[data-fuel-07-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-fuel-07-panel]'));

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => this.activate(tab));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    }
  }

  activate(selectedTab, moveFocus = false) {
    for (const tab of this.tabs) {
      const selected = tab === selectedTab;
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);

      tab.setAttribute('aria-selected', selected.toString());
      tab.tabIndex = selected ? 0 : -1;
      if (panel) panel.hidden = !selected;
    }

    if (moveFocus) selectedTab.focus();

    const activePanel = this.querySelector(`#${CSS.escape(selectedTab.getAttribute('aria-controls'))}`);
    activePanel?.querySelector('fuel-07-carousel')?.updateNavigation();
  }

  onKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    let nextIndex;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    this.activate(this.tabs[nextIndex], true);
  }
}

class Fuel07Grid extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.list = this.querySelector('.fuel-07-grid__list');
    this.button = this.querySelector('[data-fuel-07-grid-more]');
    this.status = this.querySelector('[data-fuel-07-grid-status]');
    this.total = Number.parseInt(this.dataset.total, 10) || 0;

    if (!this.list || !this.button) return;

    this.label = this.button.textContent.trim();
    this.button.addEventListener('click', () => this.showMore());
    this.updateStatus();
  }

  async showMore() {
    const nextUrl = this.button.dataset.nextUrl;
    if (!nextUrl || this.button.hasAttribute('aria-busy')) return;

    this.button.setAttribute('aria-busy', 'true');
    this.button.textContent = 'Loading…';

    try {
      const response = await this.requestPage(nextUrl);
      if (!response.ok) throw new Error(`Grid request failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const added = [];

      for (const item of doc.querySelectorAll('[data-fuel-07-grid-item]')) {
        const node = document.importNode(item, true);
        this.list.appendChild(node);
        added.push(node);
      }

      const followingUrl = doc.querySelector('[data-fuel-07-next-url]')?.getAttribute('data-fuel-07-next-url');

      if (followingUrl && added.length > 0) {
        this.button.dataset.nextUrl = followingUrl;
        this.button.removeAttribute('aria-busy');
        this.button.textContent = this.label;
      } else {
        this.button.hidden = true;
      }

      this.clearError();
      this.updateStatus();
      // Growave hydrates wishlist placeholders through its own DOM observer,
      // so appended cards pick up their heart without an explicit refresh call.
      added[0]?.querySelector('a')?.focus({ preventScroll: true });
    } catch (error) {
      this.button.removeAttribute('aria-busy');
      this.button.textContent = this.label;
      this.showError(error);
    }
  }

  // Shopify answers request bursts with 429, and storefront pages occasionally
  // 5xx. One delayed retry clears both without the shopper noticing.
  async requestPage(url, retry = true) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (response.ok || !retry) return response;
    if (response.status !== 429 && response.status < 500) return response;

    await new Promise((resolve) => setTimeout(resolve, 800));
    return this.requestPage(url, false);
  }

  showError(error) {
    console.warn('Fuel07Grid: could not load the next page', error);
    if (!this.status) return;

    this.status.classList.remove('visually-hidden');
    this.status.classList.add('fuel-07-grid__status--error');
    this.status.textContent = 'Could not load more products. Tap Show More to try again.';
  }

  clearError() {
    if (!this.status) return;

    this.status.classList.add('visually-hidden');
    this.status.classList.remove('fuel-07-grid__status--error');
  }

  updateStatus() {
    if (!this.status) return;

    const shown = this.list.querySelectorAll('[data-fuel-07-grid-item]').length;
    this.status.textContent = this.total
      ? `Showing ${shown} of ${this.total} products`
      : `Showing ${shown} products`;
  }
}

class Fuel07Recommendations extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.renderRecommendation(), { once: true });
      return;
    }

    this.renderRecommendation();
  }

  renderRecommendation() {
    const templates = Array.from(this.querySelectorAll('template[data-collection-handle]'));
    const fallback = templates.find((template) => template.dataset.fallback === 'true') ?? templates[0];
    const candidates = templates.filter((template) => template.dataset.fallback !== 'true');
    const history = this.getHistory();
    const selected = this.selectCollection(candidates, history) ?? fallback;

    if (!selected) {
      this.remove();
      return;
    }

    const target = this.querySelector('[data-fuel-07-content]');
    target?.replaceChildren(selected.content.cloneNode(true));
    this.dataset.selectedCollection = selected.dataset.collectionHandle;
  }

  getHistory() {
    const paths = [];

    try {
      const tracked = JSON.parse(localStorage.getItem('fuel_07_browsing_history') ?? '[]');
      if (Array.isArray(tracked)) paths.push(...tracked.slice().reverse());

      const stored = JSON.parse(localStorage.getItem('recently_viewed') ?? '[]');
      if (Array.isArray(stored)) paths.push(...stored.slice().reverse().map(({ productUrl }) => productUrl));
    } catch (error) {
      return paths.map((path) => this.normalizePath(path)).filter(Boolean);
    }

    return Array.from(new Set(paths.map((path) => this.normalizePath(path)).filter(Boolean)));
  }

  selectCollection(templates, history) {
    let selected;
    let selectedScore = 0;

    for (const template of templates) {
      const products = this.getCollectionProducts(template);
      const score = history.reduce((total, path, index) => {
        if (!products.has(path)) return total;
        return total + history.length - index;
      }, 0);

      if (score > selectedScore) {
        selected = template;
        selectedScore = score;
      }
    }

    return selected;
  }

  getCollectionProducts(template) {
    try {
      const data = template.content.querySelector('[data-fuel-07-products]')?.textContent ?? '[]';
      return new Set(JSON.parse(data).map((url) => this.normalizePath(url)).filter(Boolean));
    } catch (error) {
      return new Set();
    }
  }

  normalizePath(url) {
    if (!url) return '';

    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
    } catch (error) {
      return '';
    }
  }
}

if (!customElements.get('fuel-07-carousel')) customElements.define('fuel-07-carousel', Fuel07Carousel);
if (!customElements.get('fuel-07-tabs')) customElements.define('fuel-07-tabs', Fuel07Tabs);
if (!customElements.get('fuel-07-grid')) customElements.define('fuel-07-grid', Fuel07Grid);
if (!customElements.get('fuel-07-recommendations')) {
  customElements.define('fuel-07-recommendations', Fuel07Recommendations);
}
})();
